import { useAuthorization } from 'framework/authorization';
import { useCrudService } from 'framework/crud';
import { CREATE_EXPENSE, FIND_EMPLOYEE_EXPENSES_BY_LEAVE_IDS, useExpenseService } from 'domain/shared/expense';
import { LeaveDto, LeaveDtoWithCompensation } from 'domain/shared/leave';
import { usePeopleManagementApi } from 'infrastructure/api';
import { ListData } from 'framework/list';

const useSharedLeaveService = () => {
  const { checkLeaveCompensations } = useExpenseService();
  const { isGranted } = useAuthorization();

  const { search: baseSearch, searchAll } = useCrudService<LeaveDto>({
    singleResourceUrl: '/leaves/:id',
    listResourceUrl: '/leaves',
    useService: usePeopleManagementApi,
    entityName: 'Leave',
  });

  const searchWithExpensesInfo = async (params: any): Promise<ListData<LeaveDtoWithCompensation>> => {
    try {
      if (!isGranted(FIND_EMPLOYEE_EXPENSES_BY_LEAVE_IDS) || !isGranted(CREATE_EXPENSE)) {
        return baseSearch({ searchRequest: params });
      }

      const result = await baseSearch({ searchRequest: params });

      try {
        const expensesResult = await checkLeaveCompensations(result.result.map(({ id }: any) => id));

        return {
          ...result,
          result: result.result.map((item: { id: any }) => ({
            ...item,
            hasCompensation: !!expensesResult.find(({ leaveId }: any) => String(leaveId) === String(item.id))
              ?.compensationExists,
          })),
        };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }

      return result;
    } catch {
      return {
        result: [],
      };
    }
  };

  const searchAllWithExpensesInfo = async (params: any): Promise<LeaveDtoWithCompensation[]> => {
    let totalPages = 0;
    let currentPage = 0;
    let result: LeaveDtoWithCompensation[] = [];

    do {
      // eslint-disable-next-line no-await-in-loop
      const response = await searchWithExpensesInfo({
        ...params,
        pageNumber: currentPage,
      });

      currentPage += 1;
      totalPages = response.totalPages || 0;
      result = [
        ...result,
        ...(response.result?.filter((item: LeaveDtoWithCompensation) => !item.hasCompensation) || []),
      ];
    } while (currentPage < totalPages);

    return result;
  };

  return {
    searchAll,
    searchWithExpensesInfo,
    searchAllWithExpensesInfo,
  };
};

export default useSharedLeaveService;
